import type { EventRequest } from '@apm/contracts';
import type { ReceiverError, TransitionMetrics } from '@apm/bloom-receiver-sdk';

/**
 * 디버그 패널이 읽는 계측 버퍼.
 *
 * **React state 로 두지 않는다.** 전환·이벤트마다 렌더가 돌면 그 렌더가 측정 대상인
 * 프레임을 건드린다 (`docs/06-frontend-spec.md`: "매 프레임 DOM 갱신은 그 자체가 측정
 * 교란이다"). 여기 쌓아 두고 패널이 낮은 주기로 폴링해 그린다.
 *
 * 패널이 꺼져 있으면 push 만 일어나고 렌더는 없다 — 그래서 `?debug=1` 여부가
 * 재생 경로의 타이밍을 바꾸지 않는다.
 */

/** 보관 상한. 30초 자산 기준 두 시간이 넘는 분량이라 실기 확인에 부족하지 않다. */
const KEEP = 200;

export interface Telemetry {
  transitions: TransitionMetrics[];
  events: EventRequest[];
  errors: ReceiverError[];
  push: {
    transition(m: TransitionMetrics): void;
    event(e: EventRequest): void;
    error(e: ReceiverError): void;
  };
}

export function createTelemetry(): Telemetry {
  const t: Telemetry = {
    transitions: [],
    events: [],
    errors: [],
    push: {
      transition(m) {
        t.transitions.push(m);
        if (t.transitions.length > KEEP) t.transitions.shift();
      },
      event(e) {
        t.events.push(e);
        if (t.events.length > KEEP) t.events.shift();
      },
      error(e) {
        t.errors.push(e);
        if (t.errors.length > KEEP) t.errors.shift();
      },
    },
  };
  return t;
}

/**
 * 백분위. `docs/spike-result.md` 와 같은 방식(정렬 후 인덱스)으로 뽑는다 —
 * M7 이 M0 의 p50·p95 와 대조하므로 계산 방식이 갈라지면 비교가 성립하지 않는다.
 */
export function percentile(values: number[], p: number): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const idx = Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length));
  return sorted[idx] ?? null;
}
